import { useState, useEffect } from "react";
import { CircularProgress, LinearProgress } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import DragAndDropTable from "components/Modules/DragAndDropTable";
import { repositionImage } from "services/models/modelDetails/modelTasks/images";
import { connect, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { showError } from "../../../../../redux/common/actions";
import GeneralButton from "components/Elements/GeneralButton";
import AddEditModel from "./AddEditModel";
import DeleteDialog from "components/Elements/DeleteDialog";
import DetailsPanel from "components/Elements/DetailsPanel";
import withMount from "components/HOC/withMount";
import { pasteModelTaskImage } from "services/models/modelDetails/modelTasks/pasteApi";
import ImageViewer from "components/Elements/ImageViewer";
import {
	EDIT_ACCESS,
	FULL_ACCESS,
	READONLY_ACCESS,
} from "constants/AccessTypes/AccessTypes";
import useModelTaskImages from "hooks/ModuleTaskTableHooks/useModalTaskImages";
import { getLocalStorageData } from "helpers/utils";

const useStyles = makeStyles()((theme) => ({
	images: {
		display: "flex",
		flexDirection: "column",
		marginBottom: 12,
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
	},
}));

const Images = ({ taskInfo, getError, isMounted }) => {
	const me = getLocalStorageData("me");
	const access = me?.position?.modelAccess;
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [pastePart, setPastePart] = useState(true);
	const [isPasting, setIsPasting] = useState(false);

	const {
		openImage,
		setOPenImage,
		errorResponse,
		ImageToOpen,
		setImageToOpen,
		images,
		setImage,
		apiResponse,
		fetchTaskImages,
		setState,
		CtxDispatch,
		state,
		MtxDispatch,
	} = useModelTaskImages({ taskInfo, getError, isMounted });

	useEffect(() => {
		setPastePart(state.isImageTaskDisabled);
	}, [state]);

	const handleDragEnd = async (e) => {
		if (!e.destination) {
			return;
		}
		if (e.destination.index === e.source.index) return;

		const result = [...images.data];
		const [removed] = result.splice(e.source.index, 1);
		result.splice(e.destination.index, 0, removed);
		setState({ data: result });

		try {
			let payloadBody = {
				id: e.draggableId,
				destinationId: e?.destination?.destinationId,
				direction: e?.destination?.direction,
			};
			const response = await repositionImage(e.draggableId, payloadBody);
			if (!isMounted.aborted) {
				if (response.status) {
					const newDate = result.map((x, i) =>
						i === e.destination.index ? { ...x, pos: response.data.pos } : x
					);
					setState({ originalData: newDate, data: newDate });
				} else {
					setState({ data: images.originalData });
				}
			}
		} catch (error) {
			return;
		}
	};

	const handleClose = () => {
		setState({ open: false, imageId: null });
	};

	const handleEdit = (id) => {
		setState({ open: true, imageId: id });
	};

	const handleAddEditComplete = (data) => {
		if (images.imageId) {
			const updatedData = images.data.map((x) =>
				x.id === images.imageId ? { ...x, description: data } : x
			);
			setImage((prev) => ({
				...prev,
				data: updatedData,
				originalData: updatedData,
			}));
		} else {
			const mainData = [...images.data];
			mainData.push(apiResponse(data));
			setState({
				count: images.count + 1,
				data: mainData,
				originalData: mainData,
			});
			CtxDispatch({
				type: "TAB_COUNT",
				payload: {
					countTab: "imageCount",
					data: mainData?.length,
				},
			});
			if (mainData.length > 0) {
				CtxDispatch({ type: "SET_IMAGES", payload: true });
			} else {
				CtxDispatch({ type: "SET_IMAGES", payload: false });
			}
		}
	};

	const handleDelete = (id) => {
		setState({ delete: true, imageId: id });
	};

	const handleRemoveData = (id) => {
		const filteredData = images.data.filter((x) => x.id !== id);
		setState({ data: filteredData, count: images.count - 1 });
		CtxDispatch({
			type: "TAB_COUNT",
			payload: {
				countTab: "imageCount",
				data: filteredData?.length,
			},
		});
		if (filteredData.length === 0) {
			CtxDispatch({ type: "SET_IMAGES", payload: false });
		}
	};

	const handleDeleteDialogClose = () => {
		setState({ delete: false, imageId: null });
	};

	const handleCopy = (id) => {
		localStorage.setItem("taskimage", id);
		MtxDispatch({ type: "DISABLE_IMAGES_TASK", payload: false });
	};

	const handlePaste = async () => {
		setIsPasting(true);
		try {
			const taskPartId = localStorage.getItem("taskimage");
			let result = await pasteModelTaskImage(taskInfo.id, {
				modelVersionTaskImageID: taskPartId,
			});

			if (!isMounted.aborted) {
				if (result.status) {
					await fetchTaskImages(false);
					setImage((prev) => ({
						...prev,
						count: images.count + 1,
					}));
					CtxDispatch({
						type: "TAB_COUNT",
						payload: {
							countTab: "imageCount",
							data: images.count + 1,
						},
					});
				} else {
					// errorResponse(result);
					dispatch(
						showError(result?.data?.detail || t("message.couldNotPaste"))
					);
				}
			}
		} catch (e) {
			return;
		}
		setIsPasting(false);
	};

	const checkcopyPartStatus = async () => {
		try {
			const taskId = localStorage.getItem("taskimage");

			if (taskId) {
				setPastePart(false);
			}
		} catch (error) {
			return;
		}
	};

	const visibilitychangeCheck = function () {
		if (!document.hidden) {
			checkcopyPartStatus();
		}
	};

	useEffect(() => {
		checkcopyPartStatus();
		document.addEventListener("visibilitychange", visibilitychangeCheck);
		return () =>
			document.removeEventListener("visibilitychange", visibilitychangeCheck);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (images.loading) {
		return <CircularProgress />;
	}

	const editLabel = t("action.edit");
	const copyLabel = t("action.copy");
	const removeLabel = t("action.remove");
	const imageLabel = t("label.image");

	return (
		<>
			<ImageViewer
				open={openImage}
				onClose={() => {
					setOPenImage(false);
					setImageToOpen(null);
				}}
				imgSource={ImageToOpen}
			/>
			{images.open && (
				<AddEditModel
					open={images.open}
					handleClose={handleClose}
					title={imageLabel}
					taskInfo={taskInfo}
					taskId={taskInfo.id}
					imageDetail={images.data.find((x) => x.id === images.imageId)}
					handleComplete={handleAddEditComplete}
					errorResponse={errorResponse}
				/>
			)}
			<DeleteDialog
				open={images.delete}
				entityName="Image"
				deleteID={images.imageId}
				deleteEndpoint={`/api/modelversiontaskimages`}
				handleRemoveData={handleRemoveData}
				closeHandler={handleDeleteDialogClose}
			/>
			<div className={classes.images}>
				<div className={classes.header}>
					<DetailsPanel header={t("label.images")} dataCount={images.count} />
					{(access === FULL_ACCESS || access === EDIT_ACCESS) &&
					!state?.modelDetail?.isPublished ? (
						<>
							<GeneralButton
								style={{ background: "#ED8738" }}
								onClick={handlePaste}
								disabled={pastePart}
							>
								{t("action.pasteEntity", { entity: imageLabel })}
							</GeneralButton>
							<GeneralButton onClick={() => setState({ open: true })}>
								{t("action.addEntity", { entity: imageLabel })}
							</GeneralButton>
						</>
					) : null}
				</div>
				{isPasting ? <LinearProgress /> : null}

				<DragAndDropTable
					data={images.data}
					handleDragEnd={handleDragEnd}
					headers={[t("label.image"), t("label.description")]}
					columns={[
						{ id: 1, name: "image", style: { width: "50vw" } },
						{ id: 2, name: "description", style: { width: "50vw" } },
					]}
					isModelEditable
					disableDnd={
						access === READONLY_ACCESS || state?.modelDetail?.isPublished
					}
					menuData={[
						{
							name: editLabel,
							handler: handleEdit,
							isDelete: false,
						},
						{
							name: copyLabel,
							handler: handleCopy,
						},
						{
							name: removeLabel,
							handler: handleDelete,
							isDelete: true,
						},
					].filter((x) => {
						if (state?.modelDetail?.isPublished) {
							return x?.name === copyLabel;
						}
						return access === FULL_ACCESS || access === EDIT_ACCESS;
					})}
				/>
			</div>
		</>
	);
};

const mapDispatchToProps = (dispatch) => ({
	getError: (msg) => dispatch(showError(msg)),
});

export default connect(null, mapDispatchToProps)(withMount(Images));
